import {FC, useEffect, useMemo, useState} from 'react'
import {CrewOvertime} from '../core/_requests'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const DAY_STATE_STORAGE_KEY = 'crewOvertimeView2.dayState.v1'

/* ---------- helpers ---------- */

const round = (v: number, d = 2) => {
  const f = 10 ** d
  return Math.round((Number(v || 0) + Number.EPSILON) * f) / f
}

/** YYYY-MM ➜ "MAR-2025" */
const fmtMonthLabel = (ym: string): string => {
  const [y, m] = ym.split('-')
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  return `${monthNames[Number(m) - 1]}-${y}`
}

const resolveCurrencyCode = (salaryCurrency?: string | null, salaryOtherCurrency?: string | null) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).formatToParts(0).find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const currency = (n: number, symbol = '') => `${symbol}${round(n, 2).toLocaleString('en-IN')}`

const SLOT_HOURS = 1
const MAX_SLOTS = 24

const toSlotLabel = (idx: number) => {
  const totalMinutes = idx * 60
  const hh = String(Math.floor(totalMinutes / 60)).padStart(2, '0')
  const mm = String(totalMinutes % 60).padStart(2, '0')
  return `${hh}:${mm}`
}

/* ---------- types ---------- */

interface Props {
  rows: CrewOvertime[]
  minMonth?: string
  canEdit?: boolean
  canEditRow?: (row: CrewOvertime) => boolean
  onSaveDayWise?: (row: CrewOvertime, overtimeHours: number, dayWiseSlots: DayState) => Promise<void> | void
  onCreateDayWise?: (overtimeMonth: string, overtimeHours: number, dayWiseSlots: DayState) => Promise<void> | void
}

type DayState = Record<string, boolean[]>

const readStoredDayState = (key: string): DayState | null => {
  try {
    const raw = localStorage.getItem(DAY_STATE_STORAGE_KEY)
    if (!raw) return null
    const all = JSON.parse(raw) as Record<string, DayState>
    return all[key] || null
  } catch {
    return null
  }
}

const writeStoredDayState = (key: string, state: DayState) => {
  try {
    const raw = localStorage.getItem(DAY_STATE_STORAGE_KEY)
    const all = raw ? (JSON.parse(raw) as Record<string, DayState>) : {}
    all[key] = state
    localStorage.setItem(DAY_STATE_STORAGE_KEY, JSON.stringify(all))
  } catch {
    // ignore storage failures
  }
}

/* ---------- component ---------- */

const CrewOvertimeView2: FC<Props> = ({rows, minMonth, canEdit = false, canEditRow, onSaveDayWise, onCreateDayWise}) => {
  const [daysState, setDaysState] = useState<DayState>({})
  const [initialDaysState, setInitialDaysState] = useState<DayState>({})

  const currentMonth = new Date().toISOString().slice(0, 7)
  const [monthPicker, setMonthPicker] = useState(() => {
    if (minMonth && currentMonth < minMonth) return minMonth
    return currentMonth
  })
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [isSaving, setIsSaving] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [datePage, setDatePage] = useState(1)

  useEffect(() => {
    if (!minMonth) return
    if (monthPicker < minMonth) {
      setMonthPicker(minMonth)
    }
  }, [monthPicker, minMonth])

  const [year, month] = monthPicker.split('-').map(Number)
  const now = new Date()
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month

  useEffect(() => {
    const start = `${monthPicker}-01`
    const monthLast = new Date(year, month, 0)
    const monthLastIso = `${monthPicker}-${String(monthLast.getDate()).padStart(2, '0')}`
    const todayIso = new Date().toISOString().slice(0, 10)
    const end = isCurrentMonth && todayIso < monthLastIso ? todayIso : monthLastIso
    setFromDate(start)
    setToDate(end)
  }, [monthPicker, year, month, isCurrentMonth])

  const monthRows = useMemo(() => {
    return rows
      .filter((r) => (r.overtimeMonth || '').slice(0, 7) === monthPicker)
      .sort((a, b) => {
        const byName = (a.crewName || '').localeCompare(b.crewName || '')
        if (byName !== 0) return byName
        return Number(a.id || 0) - Number(b.id || 0)
      })
  }, [rows, monthPicker])

  useEffect(() => {
    setCurrentPage(1)
    setDatePage(1)
  }, [monthPicker, fromDate, toDate])

  useEffect(() => {
    const total = monthRows.length || 1
    if (currentPage > total) setCurrentPage(total)
  }, [monthRows.length, currentPage])

  const selectedRow = useMemo(() => {
    if (!monthRows.length) return null

    // Prefer an editable row so Clear/Save actions are available when possible.
    if (canEdit) {
      const editableRow = monthRows.find((row) =>
        canEditRow ? canEditRow(row) : row.status === 'DRAFT'
      )
      if (editableRow) return editableRow
    }

    return monthRows[Math.max(0, currentPage - 1)]
  }, [monthRows, currentPage, canEdit, canEditRow])

  const visibleSlots = useMemo(() => MAX_SLOTS, [])

  const slotIndexes = useMemo(
    () => Array.from({length: visibleSlots}, (_, i) => i),
    [visibleSlots]
  )

  const dayKeys = useMemo(() => {
    const out: string[] = []
    if (!fromDate || !toDate) return out
    const start = new Date(`${fromDate}T00:00:00`)
    const end = new Date(`${toDate}T00:00:00`)
    const cursor = new Date(start)
    while (cursor <= end) {
      const y = cursor.getFullYear()
      const m = String(cursor.getMonth() + 1).padStart(2, '0')
      const d = String(cursor.getDate()).padStart(2, '0')
      out.push(`${y}-${m}-${d}`)
      cursor.setDate(cursor.getDate() + 1)
    }
    return out
  }, [fromDate, toDate])

  const dateTotalPages = Math.max(1, Math.ceil(dayKeys.length / rowsPerPage))
  const pagedDayKeys = useMemo(() => {
    const start = (datePage - 1) * rowsPerPage
    return dayKeys.slice(start, start + rowsPerPage)
  }, [dayKeys, datePage, rowsPerPage])

  useEffect(() => {
    if (datePage > dateTotalPages) setDatePage(dateTotalPages)
  }, [datePage, dateTotalPages])

  const dayStateStorageScope = useMemo(() => {
    if (!fromDate || !toDate) return ''
    return `${monthPicker}|${fromDate}|${toDate}|${visibleSlots}`
  }, [monthPicker, fromDate, toDate, visibleSlots])

  const dayStateStorageId = useMemo(() => {
    if (!dayStateStorageScope) return ''
    if (selectedRow?.id) return `${selectedRow.id}|${dayStateStorageScope}`
    return `draft|${dayStateStorageScope}`
  }, [selectedRow?.id, dayStateStorageScope])

  const fallbackDayStateStorageId = useMemo(() => {
    if (!dayStateStorageScope || !selectedRow?.id) return ''
    return `draft|${dayStateStorageScope}`
  }, [selectedRow?.id, dayStateStorageScope])

  const sanitizeStoredDayState = (stored: DayState): DayState => {
    const normalized: DayState = {}
    dayKeys.forEach((day) => {
      const row = Array.from({length: visibleSlots}, (_, i) => !!stored?.[day]?.[i])
      normalized[day] = row
    })
    return normalized
  }

  useEffect(() => {
    if (selectedRow?.dayWiseSlots && Object.keys(selectedRow.dayWiseSlots).length > 0) {
      const normalized = sanitizeStoredDayState(selectedRow.dayWiseSlots)
      setDaysState(normalized)
      setInitialDaysState(normalized)
      if (dayStateStorageId) {
        writeStoredDayState(dayStateStorageId, normalized)
      }
      return
    }

    if (dayStateStorageId) {
      const stored = readStoredDayState(dayStateStorageId)
      if (stored) {
        const normalized = sanitizeStoredDayState(stored)
        setDaysState(normalized)
        setInitialDaysState(normalized)
        return
      }
    }

    if (fallbackDayStateStorageId) {
      const storedFallback = readStoredDayState(fallbackDayStateStorageId)
      if (storedFallback) {
        const normalized = sanitizeStoredDayState(storedFallback)
        setDaysState(normalized)
        setInitialDaysState(normalized)
        if (dayStateStorageId) {
          writeStoredDayState(dayStateStorageId, normalized)
        }
        return
      }
    }

    const initial: DayState = {}
    dayKeys.forEach((k) => {
      initial[k] = Array.from({length: visibleSlots}, () => false)
    })

    const sourceHours = Number(selectedRow?.otHours || 0)
    let slotsToFill = Math.round(Math.max(sourceHours, 0) / SLOT_HOURS)
    for (const day of dayKeys) {
      if (slotsToFill <= 0) break
      for (let i = 0; i < visibleSlots && slotsToFill > 0; i++) {
        initial[day][i] = true
        slotsToFill--
      }
    }

    setDaysState(initial)
    setInitialDaysState(initial)
  }, [selectedRow, dayKeys, visibleSlots, dayStateStorageId, fallbackDayStateStorageId])

  const dayTotal = (day: string) => {
    const slots = (daysState[day] || []).filter(Boolean).length
    return round(slots * SLOT_HOURS, 2)
  }

  const monthHoursTotal = useMemo(() => {
    const totalSlots = dayKeys.reduce((sum, day) => sum + (daysState[day] || []).filter(Boolean).length, 0)
    return round(totalSlots * SLOT_HOURS, 2)
  }, [dayKeys, daysState])

  const hourRate = Number(selectedRow?.overtimeRate ?? selectedRow?.otPerHour ?? 0)
  const monthAmountTotal = round(monthHoursTotal * hourRate, 2)

  const currencySymbol = useMemo(() => {
    if (!selectedRow) return ''
    return getCurrencySymbol(resolveCurrencyCode(selectedRow.salaryCurrency, selectedRow.salaryOtherCurrency))
  }, [selectedRow])

  const isDirty = useMemo(
    () => JSON.stringify(daysState) !== JSON.stringify(initialDaysState),
    [daysState, initialDaysState]
  )

  const canModify =
    !!selectedRow &&
    canEdit &&
    (canEditRow ? canEditRow(selectedRow) : selectedRow.status === 'DRAFT')
  const canCreateFromView2 = !selectedRow && canEdit && !!onCreateDayWise
  const canModifyGrid = canModify || canCreateFromView2

  const onClearChanges = () => {
    if (!canModifyGrid) return
    setDaysState(initialDaysState)
  }

  const onSaveChanges = async () => {
    if (!canModifyGrid || !isDirty) return
    setIsSaving(true)
    try {
      if (selectedRow && onSaveDayWise) {
        await onSaveDayWise(selectedRow, monthHoursTotal, daysState)
      } else if (!selectedRow && onCreateDayWise) {
        await onCreateDayWise(`${monthPicker}-01`, monthHoursTotal, daysState)
      }
      setInitialDaysState(daysState)
      if (dayStateStorageId) {
        writeStoredDayState(dayStateStorageId, daysState)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const toggleCell = (day: string, slotIndex: number) => {
    if (!canModifyGrid) return
    setDaysState((prev) => {
      const curr = prev[day] || Array.from({length: visibleSlots}, () => false)
      const next = [...curr]
      next[slotIndex] = !next[slotIndex]
      return {...prev, [day]: next}
    })
  }

  const formatDayLabel = (day: string) => {
    const dt = new Date(`${day}T00:00:00`)
    return dt.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'})
  }

  /* ---------- styles ---------- */

  const thStyle: React.CSSProperties = {
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    verticalAlign: 'middle',
    backgroundColor: '#f4f6fa',
    boxShadow: 'inset 0 0 0 9999px #f4f6fa',
    borderBottom: '2px solid #dee2e6',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    color: '#3f4254',
    letterSpacing: '0.3px',
  }

  const slotHeaderStyle: React.CSSProperties = {
    ...thStyle,
    minWidth: '60px',
    borderLeft: '2px solid #dee2e6',
    fontSize: '12px',
    fontWeight: 500,
  }

  const tdStyle: React.CSSProperties = {
    padding: '8px 10px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    verticalAlign: 'middle',
    color: '#3f4254',
  }

  const tdLeftStyle: React.CSSProperties = {
    ...tdStyle,
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    backgroundColor: '#fff',
    boxShadow: 'inset 0 0 0 9999px #fff',
    zIndex: 1,
  }

  const totalRowStyle: React.CSSProperties = {
    ...tdStyle,
    fontWeight: 700,
    backgroundColor: '#f0f4ff',
    boxShadow: 'inset 0 0 0 9999px #f0f4ff',
    borderTop: '2px solid #bbc3d4',
    fontSize: '13px',
  }

  const slotButtonStyle = (active: boolean, disabled: boolean): React.CSSProperties => ({
    width: '40px',
    height: '24px',
    borderRadius: '4px',
    border: '1px solid #bfc5ce',
    backgroundColor: active ? '#ffe3c4' : '#e9edf2',
    color: '#4a5568',
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.75 : 1,
    pointerEvents: 'auto',
  })

  const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    const container = event.currentTarget
    if (container.scrollWidth <= container.clientWidth) return

    // Keep vertical scrolling native; use Shift + wheel for horizontal intent.
    const isShiftHorizontalIntent = event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
    if (!isShiftHorizontalIntent || event.deltaY === 0) return

    const isScrollingRight = event.deltaY > 0
    const canScrollContainerRight = container.scrollLeft + container.clientWidth < container.scrollWidth - 1
    const canScrollContainerLeft = container.scrollLeft > 0
    if ((isScrollingRight && !canScrollContainerRight) || (!isScrollingRight && !canScrollContainerLeft)) return

    event.preventDefault()
    container.scrollLeft += event.deltaY
  }

  return (
    <div>
      <div className='d-flex flex-wrap gap-3 align-items-end mb-4'>
        <div style={{minWidth: '220px'}}>
          <label className='form-label text-muted fw-semibold fs-7 mb-2'>Month</label>
          <input
            type='month'
            className='form-control form-control-sm'
            value={monthPicker}
            min={minMonth || undefined}
            onChange={(e) => {
              const nextMonth = e.target.value || currentMonth
              if (minMonth && nextMonth < minMonth) {
                setMonthPicker(minMonth)
                return
              }
              setMonthPicker(nextMonth)
            }}
          />
        </div>
        <div style={{minWidth: '220px'}}>
          <label className='form-label text-muted fw-semibold fs-7 mb-2'>From date</label>
          <input
            type='date'
            className='form-control form-control-sm'
            value={fromDate}
            min={`${monthPicker}-01`}
            max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div style={{minWidth: '220px'}}>
          <label className='form-label text-muted fw-semibold fs-7 mb-2'>To date</label>
          <input
            type='date'
            className='form-control form-control-sm'
            value={toDate}
            min={fromDate || `${monthPicker}-01`}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <div className='ms-auto d-flex gap-4 align-items-center'>
          <span className='badge badge-light-primary px-4 py-2'>
            Total Overtime: {monthHoursTotal} hrs ({currency(monthAmountTotal, currencySymbol)})
          </span>
        </div>
      </div>

      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div className='text-muted small'>
          {selectedRow
            ? `Crew: ${selectedRow.crewName || '-'} | Rank: ${selectedRow.rankName || '-'} | Month: ${fmtMonthLabel(monthPicker)}`
            : 'No crew records in selected month'}
        </div>
      </div>

      <div
        style={{
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 380px)',
          minHeight: '320px',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
        }}
        onWheel={handleTableHorizontalWheelScroll}
      >
        <table className='table mb-0' style={{minWidth: '1400px', borderCollapse: 'collapse'}}>
          <thead>
            <tr>
              <th style={{...thStyle, minWidth: '190px', position: 'sticky', left: 0, zIndex: 3}}>
                Date
              </th>
              {slotIndexes.map((idx) => (
                <th key={idx} style={slotHeaderStyle}>
                  {toSlotLabel(idx)}
                </th>
              ))}
              <th style={{...thStyle, minWidth: '150px', borderLeft: '2px solid #dee2e6', backgroundColor: '#e8ecf4', boxShadow: 'inset 0 0 0 9999px #e8ecf4'}}>
                Total Overtime
              </th>
              <th style={{...thStyle, minWidth: '150px', borderLeft: '2px solid #dee2e6', backgroundColor: '#e8ecf4', boxShadow: 'inset 0 0 0 9999px #e8ecf4'}}>
                Total Spend
              </th>
            </tr>
          </thead>

          <tbody>
            {pagedDayKeys.length === 0 ? (
              <tr>
                <td colSpan={3 + visibleSlots} className='text-center text-muted' style={{padding: '40px 16px', fontSize: '14px'}}>
                  No dates available for the selected filters.
                </td>
              </tr>
            ) : (
              <>
                {pagedDayKeys.map((day, idx) => (
                  <tr key={day} style={{borderBottom: '1px solid #eef0f4'}}>
                    <td style={{...tdLeftStyle, left: 0, fontWeight: 600}}>{formatDayLabel(day)}</td>
                    {slotIndexes.map((slotIdx) => {
                      const active = !!daysState[day]?.[slotIdx]
                      const disabled = !canModifyGrid
                      return (
                        <td key={slotIdx} style={tdStyle}>
                          <button
                            type='button'
                            style={slotButtonStyle(active, disabled)}
                            onClick={() => toggleCell(day, slotIdx)}
                            disabled={disabled}
                          >
                            {active ? 'OT' : 'N'}
                          </button>
                        </td>
                      )
                    })}
                    <td style={{...tdStyle, fontWeight: 600, borderLeft: '2px solid #dee2e6', backgroundColor: '#f7f9fc'}}>
                      {dayTotal(day)} hrs
                    </td>
                    <td style={{...tdStyle, fontWeight: 600, borderLeft: '2px solid #dee2e6', backgroundColor: '#f7f9fc'}}>
                      {currency(dayTotal(day) * hourRate, currencySymbol)}
                    </td>
                  </tr>
                ))}

                <tr>
                  <td style={{...totalRowStyle, textAlign: 'left', fontWeight: 700, position: 'sticky', left: 0, fontSize: '14px'}}>
                    TOTAL
                  </td>
                  {slotIndexes.map((slotIdx) => (
                    <td key={slotIdx} style={{...totalRowStyle, borderLeft: slotIdx === 0 ? '2px solid #bbc3d4' : undefined}}>
                      N
                    </td>
                  ))}
                  <td style={{...totalRowStyle, borderLeft: '2px solid #bbc3d4'}}>
                    {monthHoursTotal} hrs ({currency(monthAmountTotal, currencySymbol)})
                  </td>
                  <td style={{...totalRowStyle, borderLeft: '2px solid #bbc3d4'}}>
                    {currency(monthAmountTotal, currencySymbol)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 mt-2'>
        <div className='d-flex align-items-center gap-2' style={{color: '#4a5b75'}}>
          <span
            style={{
              display: 'inline-block',
              width: 28,
              height: 28,
              border: '1px solid #9fb0c8',
              borderRadius: 6,
              background: '#dfe7f1',
              textAlign: 'center',
              lineHeight: '26px',
              fontSize: 12,
              fontWeight: 700,
              color: '#4f6484',
            }}
          >
            N
          </span>
          <span style={{fontWeight: 500}}>Normal</span>
          <span
            style={{
              display: 'inline-block',
              width: 28,
              height: 28,
              border: '1px solid #d6a15f',
              borderRadius: 6,
              background: '#ffd8a8',
              textAlign: 'center',
              lineHeight: '26px',
              fontSize: 12,
              fontWeight: 700,
              color: '#8a5a1f',
              marginLeft: 8,
            }}
          >
            OT
          </span>
          <span style={{fontWeight: 500}}>Overtime</span>
        </div>

        {canEdit ? (
          <div className='d-flex align-items-center gap-2'>
            <button
              className='btn btn-sm'
              style={{
                backgroundColor: !canModify || !isDirty || isSaving ? '#eef2f7' : '#e6edf6',
                border: '1px solid #bcc9da',
                color: '#3f5677',
                fontWeight: 600,
              }}
              onClick={onClearChanges}
              disabled={!canModifyGrid || !isDirty || isSaving}
            >
              Clear Changes
            </button>
            <button
              className='btn btn-sm'
              style={{
                backgroundColor: !canModifyGrid || !isDirty || isSaving || !(onSaveDayWise || onCreateDayWise) ? '#eef2f7' : '#e6edf6',
                border: '1px solid #bcc9da',
                color: '#3f5677',
                fontWeight: 600,
              }}
              onClick={onSaveChanges}
              disabled={!canModifyGrid || !isDirty || isSaving || !(onSaveDayWise || onCreateDayWise)}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ) : null}
      </div>

      <div className='pagination-wrapper d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 border-top'
        style={{position: 'sticky', bottom: 0, zIndex: 6, backgroundColor: '#ffffff'}}>
        <div className='d-flex align-items-center gap-2'>
          <span className='text-muted'>Rows per page</span>
          <SearchableSelect
            className='form-select form-select-sm'
            style={{width: '90px'}}
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value))
              setDatePage(1)
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={31}>31</option>
          </SearchableSelect>
        </div>

        <div className='d-flex align-items-center gap-3'>
          <button
            type='button'
            className='btn btn-sm'
            style={{
              backgroundColor: datePage <= 1 ? '#eef2f7' : '#e6edf6',
              border: '1px solid #bcc9da',
              color: '#3f5677',
              fontWeight: 600,
            }}
            disabled={datePage <= 1}
            onClick={() => setDatePage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className='text-muted small'>Page {datePage} of {dateTotalPages}</span>
          <button
            type='button'
            className='btn btn-sm'
            style={{
              backgroundColor: datePage >= dateTotalPages ? '#eef2f7' : '#e6edf6',
              border: '1px solid #bcc9da',
              color: '#3f5677',
              fontWeight: 600,
            }}
            disabled={datePage >= dateTotalPages}
            onClick={() => setDatePage((p) => Math.min(dateTotalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrewOvertimeView2
